// sessionValidation.js
// -----------------------------------------------------------------------------
// Validation de la session AVANT tout affichage de contenu authentifie.
//
// Probleme corrige :
//   A l'ouverture de l'application (favori / onglet restaure) avec un token
//   expire en localStorage, l'application affichait d'abord le layout + le
//   tableau de bord, puis le Header appelait "api/verif" et redirigeait vers
//   /login. L'utilisateur voyait donc un "flash" de la plateforme.
//
// Principe :
//   - Pas de token en local  -> session invalide immediatement (aucun appel).
//   - Token present          -> un seul appel "api/verif" partage par toute
//                               l'application (promesse mise en cache), et le
//                               rendu authentifie attend son resultat.
// -----------------------------------------------------------------------------
import axios from "axios";
import { BACKEND_URL_PORTAIL } from "../config/const";

// Delai maximal accorde a la verification avant de considerer la session
// comme invalide (evite un ecran de chargement infini).
const VERIF_TIMEOUT_MS = 15000;

export const SESSION_CHECKING = "checking";
export const SESSION_VALID = "valid";
export const SESSION_INVALID = "invalid";

// Promesse partagee : la verification n'est faite qu'une seule fois par
// chargement de l'application (et non a chaque changement de route).
// Le resultat est associe au token verifie : si le token change (nouveau
// login), une nouvelle verification est declenchee automatiquement.
let sessionCheckPromise = null;
let checkedToken = null;

const getToken = () => localStorage.getItem("token");

/**
 * Supprime les donnees de session locales (token, profil, contexte metier).
 */
export const clearLocalSession = () => {
  localStorage.removeItem("token");
  localStorage.removeItem("authUser");
  localStorage.removeItem("user");
};

/**
 * Force une nouvelle verification au prochain appel (apres login / logout).
 */
export const resetSessionCheck = () => {
  sessionCheckPromise = null;
  checkedToken = null;
};

/**
 * Verifie la session aupres du back-office.
 * @returns {Promise<boolean>} true si la session est valide.
 */
export const verifySession = () => {
  const token = getToken();

  // Resultat deja connu pour ce meme token : on le reutilise.
  if (sessionCheckPromise && checkedToken === token) {
    return sessionCheckPromise;
  }

  checkedToken = token;

  // Aucun token : inutile d'appeler le serveur, la session est invalide.
  if (!token) {
    sessionCheckPromise = Promise.resolve(false);
    return sessionCheckPromise;
  }

  sessionCheckPromise = axios
    .get(`${BACKEND_URL_PORTAIL}api/verif`, {
      headers: { Authorization: `Bearer ${token}` },
      // Sans timeout, une requete qui n'aboutit jamais laisserait
      // l'utilisateur bloque sur l'ecran de chargement.
      timeout: VERIF_TIMEOUT_MS,
    })
    .then(() => true)
    .catch(() => {
      // Token expire ou revoque : on nettoie pour eviter toute boucle
      // /login -> /home -> /login.
      clearLocalSession();
      return false;
    });

  return sessionCheckPromise;
};

/**
 * Marque la session comme valide sans appel reseau (utilise juste apres un
 * login reussi, ou lorsque le Header vient de la verifier).
 */
export const markSessionValid = () => {
  checkedToken = getToken();
  sessionCheckPromise = Promise.resolve(true);
};

/**
 * Marque la session comme invalide (expiration detectee pendant l'utilisation).
 */
export const markSessionInvalid = () => {
  checkedToken = getToken();
  sessionCheckPromise = Promise.resolve(false);
};
